import { SOCIAL_EXPORT_FRAGMENT_SOURCE } from '@app/modules/social-export/lib/social-export-fragment-source';
import { webApi } from '@mi-front/api-clients';
import { LocaleRelativeTime } from '@mi-front/ui';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { socialExportPublishMessages } from './social-export-publish-messages';

export interface SocialExportMediaSelectorProps {
  /** Current selection: fragment source key or a video-editor edit id */
  value: string;
  onChange: (value: string) => void;
  edits: webApi.EnrichedVideoEditorEditListItemDto[];
  /** When false, only rendered edits are listed (composition social export). */
  includeFragmentOption?: boolean;
}

interface OptionRowContent {
  icon: ReactNode;
  primary: ReactNode;
  secondary: ReactNode;
}

function renderOptionRow(content: OptionRowContent, compact: boolean): ReactNode {
  const inner = (
    <>
      <ListItemIcon sx={compact ? { minWidth: 36 } : undefined}>{content.icon}</ListItemIcon>
      <ListItemText
        primary={content.primary}
        secondary={content.secondary}
        sx={compact ? { my: 0 } : undefined}
      />
    </>
  );
  return compact ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{inner}</Box>
  ) : (
    inner
  );
}

/**
 * Select publishable media: optional original fragment media plus completed video-editor renders.
 */
export function SocialExportMediaSelector({
  value,
  onChange,
  edits,
  includeFragmentOption = true,
}: SocialExportMediaSelectorProps) {
  const { $t } = useIntl();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const fragmentContent: OptionRowContent = {
    icon: <VideoFileIcon fontSize="small" />,
    primary: $t(socialExportPublishMessages.mediaSelectorOriginalMedia),
    secondary: '',
  };

  const getEditContent = (edit: webApi.EnrichedVideoEditorEditListItemDto): OptionRowContent => ({
    icon: <ViewTimelineOutlinedIcon fontSize="small" />,
    primary: (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          my: 0.5,
        }}
      >
        {edit.name}
        <Chip
          size="small"
          variant="outlined"
          label={$t(socialExportPublishMessages.mediaSelectorEdition)}
          sx={includeFragmentOption ? { fontSize: '11px' } : undefined}
        />
      </Box>
    ),
    secondary:
      edit.renderJob?.status === 'COMPLETED' && edit.renderJob.finishedOn ? (
        <>
          {$t({
            id: 'videoEditions.finishedOn',
            defaultMessage: 'Generado',
          })}{' '}
          <LocaleRelativeTime value={edit.renderJob.finishedOn} />
        </>
      ) : (
        <LocaleRelativeTime value={edit.updatedAt} />
      ),
  });

  const label = $t(socialExportPublishMessages.mediaSelectorLabel);
  const renderedEdits = edits.filter((edit) => edit.renderJob?.status === 'COMPLETED');
  const labelId = 'social-export-media-selector-label';

  return (
    <FormControl fullWidth sx={{ minWidth: 280 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={value}
        label={label}
        onChange={handleChange}
        renderValue={(v) => {
          if (includeFragmentOption && v === SOCIAL_EXPORT_FRAGMENT_SOURCE) {
            return renderOptionRow(fragmentContent, true);
          }
          const selectedEdit = renderedEdits.find((edit) => edit.id === v);
          if (!selectedEdit) return v;
          return renderOptionRow(getEditContent(selectedEdit), true);
        }}
      >
        {includeFragmentOption && (
          <MenuItem value={SOCIAL_EXPORT_FRAGMENT_SOURCE} sx={{ minHeight: '56px !important' }}>
            {renderOptionRow(fragmentContent, false)}
          </MenuItem>
        )}
        {renderedEdits.map((edit) => (
          <MenuItem key={edit.id} value={edit.id} sx={{ minHeight: 56 }}>
            {renderOptionRow(getEditContent(edit), false)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
